import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useCatalog } from '../context/CatalogContext';
import { rootsInSection, childrenInSection, collectDescendantIds, normalizeCategoryId } from '../lib/format';
import ProductCard from '../components/ProductCard';
import { SkeletonCard, EmptyState } from '../lib/ui/Feedback';

const CAT_TONES = ['bg-brand-light', 'bg-gold-light', 'bg-berry-light', 'bg-sky-100', 'bg-sun-light', 'bg-emerald-100'];

export default function CategoriesPage() {
  const { lang } = useLang();
  const te = lang === 'te';
  const navigate = useNavigate();
  const location = useLocation();
  const { products, categories, loading, activeSection } = useCatalog();
  const [catPath, setCatPath] = useState([]);
  const [showProducts, setShowProducts] = useState(false);

  const browseList = useMemo(() => {
    const parent = catPath.length ? catPath[catPath.length - 1] : null;
    if (!parent) return rootsInSection(categories, activeSection);
    return childrenInSection(categories, parent, activeSection);
  }, [categories, activeSection, catPath]);

  const leafId = showProducts && catPath.length ? catPath[catPath.length - 1] : null;
  const filtered = useMemo(() => {
    if (!leafId) return [];
    const allowed = collectDescendantIds(leafId, categories);
    return products.filter((p) => {
      const cid = normalizeCategoryId(p.category);
      const cat = categories.find((c) => c.id === cid);
      if (!cat || cat.section_id !== activeSection) return false;
      return allowed.has(cid);
    }).sort((a, b) => (a.display_order || 99) - (b.display_order || 99));
  }, [leafId, categories, products, activeSection]);

  const openCat = (c) => {
    const kids = childrenInSection(categories, c.id, activeSection);
    setCatPath((prev) => [...prev, c.id]);
    setShowProducts(kids.length === 0);
  };

  const goBack = () => {
    if (showProducts) { setShowProducts(false); return; }
    setCatPath((prev) => prev.slice(0, -1));
  };

  const crumbs = catPath.map((id) => {
    const c = categories.find((x) => x.id === id);
    return c ? { id, label: `${c.emoji || ''} ${te && c.label_te ? c.label_te : c.label_en}`.trim() } : { id, label: id };
  });

  const openProduct = (p) => navigate(`/product/${p.id}`, { state: { backgroundLocation: location } });

  return (
    <div className="max-w-6xl mx-auto px-3 md:px-6 pt-4">
      <h1 className="text-lg font-black text-brand-dark mb-3">{te ? 'కేటగిరీలు' : 'Categories'}</h1>

      {(catPath.length > 0 || showProducts) ? (
        <div className="flex items-center gap-1.5 flex-wrap mb-3 text-sm">
          <button type="button" onClick={goBack} className="h-7 w-7 rounded-full bg-white border border-black/10 font-black text-brand-dark shrink-0">‹</button>
          <button type="button" onClick={() => { setCatPath([]); setShowProducts(false); }} className="font-bold text-ink-soft">
            {te ? 'అన్నీ' : 'All'}
          </button>
          {crumbs.map((c, i) => (
            <React.Fragment key={c.id}>
              <span className="text-ink-faint">›</span>
              <button
                type="button"
                onClick={() => {
                  const newPath = catPath.slice(0, i + 1);
                  const kids = childrenInSection(categories, catPath[i], activeSection);
                  setCatPath(newPath);
                  setShowProducts(kids.length === 0);
                }}
                className="font-bold text-ink-soft"
              >
                {c.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      ) : null}

      {showProducts ? (
        <>
          <div className="text-sm font-extrabold text-brand-dark mb-2">{crumbs[crumbs.length - 1]?.label || (te ? 'ఉత్పత్తులు' : 'Products')}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 pb-8">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
            ) : filtered.length === 0 ? (
              <div className="col-span-full"><EmptyState icon="📦" title={te ? 'ఉత్పత్తులు లేవు' : 'No products in this category'} /></div>
            ) : (
              filtered.map((p) => <ProductCard key={p.id} product={p} onOpenDetail={() => openProduct(p)} />)
            )}
          </div>
        </>
      ) : (
        <div className="space-y-2 pb-8">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-16 rounded-2xl bg-white border border-black/5 animate-pulse" />
            ))
          ) : browseList.length === 0 ? (
            <EmptyState icon="📂" title={te ? 'వర్గాలు లేవు' : 'No categories yet'} />
          ) : browseList.map((c, i) => {
            const kids = childrenInSection(categories, c.id, activeSection);
            const label = te && c.label_te ? c.label_te : c.label_en;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => openCat(c)}
                className="w-full flex items-center gap-3 bg-white border border-black/5 shadow-soft rounded-2xl px-4 py-3.5 text-left"
              >
                <span className={`h-11 w-11 shrink-0 rounded-2xl flex items-center justify-center text-xl ${CAT_TONES[i % CAT_TONES.length]}`}>{c.emoji || '📦'}</span>
                <span className="flex-1 min-w-0">
                  <span className="block font-extrabold text-ink text-sm">{label}</span>
                  <span className="block text-xs text-ink-faint">
                    {kids.length ? `${kids.length} ${te ? 'ఉపవర్గాలు' : 'subcategories'}` : (te ? 'ఉత్పత్తులు చూడండి' : 'View products')}
                  </span>
                </span>
                <span className="text-ink-faint">›</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
